import { useLocalStorageState } from '../../../hooks/useLocalStorageState/useLocalStorageState';
import { Thumbnail } from '../../BackgroundSelectionDialog/BackgroundThumbnail/BackgroundThumbnail';
import type { AgoraRoomLike } from '../../../types';
import type { ILocalVideoTrack } from 'agora-rtc-sdk-ng';
import { useEffect, useMemo, useRef } from 'react';
import VirtualBackgroundExtension from 'agora-extension-virtual-background';

export interface BackgroundSettings {
  type: Thumbnail;
  index?: number;
}

const SELECTED_BACKGROUND_SETTINGS_KEY = 'VideoApp-selectedBackgroundSettings';

// Create one extension instance per bundle and register it once.
let vbExtension: VirtualBackgroundExtension | null = null;
function getVBExtension(): VirtualBackgroundExtension {
  if (!vbExtension) vbExtension = new VirtualBackgroundExtension();
  return vbExtension;
}

export function isVirtualBackgroundSupported(): boolean {
  try {
    return getVBExtension().checkCompatibility();
  } catch {
    return false;
  }
}

export const backgroundConfig = {
  imageNames: [
    'Abstract', 'Boho Home', 'Bookshelf', 'Coffee Shop', 'Contemporary', 'Cozy Home',
    'Desert', 'Fishing', 'Flower', 'Kitchen', 'Modern Home', 'Nature', 'Ocean',
    'Patio', 'Plant', 'San Francisco',
  ],
  images: [
    require('../../../images/thumb/Abstract.jpg'),
    require('../../../images/thumb/BohoHome.jpg'),
    require('../../../images/thumb/Bookshelf.jpg'),
    require('../../../images/thumb/CoffeeShop.jpg'),
    require('../../../images/thumb/Contemporary.jpg'),
    require('../../../images/thumb/CozyHome.jpg'),
    require('../../../images/thumb/Desert.jpg'),
    require('../../../images/thumb/Fishing.jpg'),
    require('../../../images/thumb/Flower.jpg'),
    require('../../../images/thumb/Kitchen.jpg'),
    require('../../../images/thumb/ModernHome.jpg'),
    require('../../../images/thumb/Nature.jpg'),
    require('../../../images/thumb/Ocean.jpg'),
    require('../../../images/thumb/Patio.jpg'),
    require('../../../images/thumb/Plant.jpg'),
    require('../../../images/thumb/SanFrancisco.jpg'),
  ],
};

export default function useBackgroundSettings(
  videoTrack: ILocalVideoTrack | undefined,
  _room?: AgoraRoomLike | null
): [BackgroundSettings, (s: BackgroundSettings) => void] {
  const [backgroundSettings, setBackgroundSettings] = useLocalStorageState<BackgroundSettings>(
    SELECTED_BACKGROUND_SETTINGS_KEY,
    { type: 'none', index: 0 }
  );

  const processorRef = useRef<any>(null);
  const pipedTrackIdRef = useRef<string>('');
  const initPromiseRef = useRef<Promise<void> | null>(null);

  const imageUrl = useMemo(() => {
    if (backgroundSettings.type !== 'image') return null;
    const idx = backgroundSettings.index ?? 0;
    return (backgroundConfig.images[idx] as unknown as string) ?? null;
  }, [backgroundSettings.type, backgroundSettings.index]);

  useEffect(() => {
    // No track: nothing to apply.
    if (!videoTrack) return;
    if (!isVirtualBackgroundSupported()) return;

    const extension = getVBExtension();
    // AgoraRTC.registerExtensions must be called in the same runtime as AgoraRTC;
    // We register lazily the first time we have a track.
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const AgoraRTC = require('agora-rtc-sdk-ng').default ?? require('agora-rtc-sdk-ng');
      AgoraRTC.registerExtensions?.([extension]);
    } catch {
      /* ignore */
    }

    let cancelled = false;

    const ensureProcessor = async () => {
      if (processorRef.current) return processorRef.current;
      const p = extension.createProcessor();
      processorRef.current = p;
      if (!initPromiseRef.current) {
        initPromiseRef.current = (async () => {
          // If you host WASM assets yourself, set REACT_APP_VIRTUAL_BACKGROUND_WASM_DIR
          // (example: "/assets/wasms"). If not set, the extension may still work if it can
          // locate/bundle resources; otherwise init() will throw and we just no-op.
          const wasmDir = process.env.REACT_APP_VIRTUAL_BACKGROUND_WASM_DIR;
          await p.init(wasmDir || undefined);
        })();
      }
      await initPromiseRef.current;
      return p;
    };

    const unpipeIfNeeded = async () => {
      try {
        if (pipedTrackIdRef.current) {
          videoTrack.unpipe?.();
          pipedTrackIdRef.current = '';
        }
      } catch {
        /* ignore */
      }
    };

    const apply = async () => {
      try {
        if (backgroundSettings.type === 'none') {
          const p = processorRef.current;
          if (p?.disable) await p.disable();
          await unpipeIfNeeded();
          return;
        }

        const p = await ensureProcessor();
        if (cancelled) return;

        // Ensure the processor is in the pipeline for this track.
        const trackId =
          (videoTrack as any).getTrackId?.() ??
          (videoTrack as any).getMediaStreamTrack?.()?.id ??
          'videoTrack';
        if (pipedTrackIdRef.current !== String(trackId)) {
          await unpipeIfNeeded();
          videoTrack.pipe(p).pipe((videoTrack as any).processorDestination);
          pipedTrackIdRef.current = String(trackId);
        }

        if (backgroundSettings.type === 'blur') {
          p.setOptions({ type: 'blur', blurDegree: 2 });
          await p.enable();
          return;
        }

        if (backgroundSettings.type === 'image' && imageUrl) {
          const img = new Image();
          img.src = imageUrl;
          await new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('Failed to load background image'));
          });
          if (cancelled) return;

          p.setOptions({ type: 'img', source: img, fit: 'cover' });
          await p.enable();
          return;
        }
      } catch (e) {
        // Swallow errors so the call UI never breaks due to VB issues.
        // Typical failures: missing WASM assets, low-end device, or insecure context.
        // eslint-disable-next-line no-console
        console.warn('Virtual background failed:', e);
      }
    };

    apply();

    return () => {
      cancelled = true;
    };
  }, [videoTrack, backgroundSettings.type, imageUrl]);

  return [backgroundSettings, setBackgroundSettings];
}
